import { TiptapTransformer } from "@hocuspocus/transformer";
import * as Y from "yjs";
import getProseMirrorSchema, { extensions, stripUnknownMarks } from "../../../src/lib/getProseMirrorSchema.js";
import {
  manageProjectDocumentsSummaryIndicatesManuscriptApplied,
  toolExecutionSummaryIndicatesSelectionRewriteApplied,
} from "~shared/agenticManuscriptPlacementSignals";

/** Commit pre-agent-edit undo snapshot if Yjs sync did not record it in time. */
export function maybeRecordAgentManuscriptEditUndo(
  tool: string,
  newStatus: string,
  outcome?: string,
): void {
  if (newStatus !== "complete") return;
  if (outcome?.toLowerCase().startsWith("error")) return;
  if (tool === "generate_write_button_prose") {
    if (!toolExecutionSummaryIndicatesSelectionRewriteApplied(outcome ?? "")) return;
  } else if (tool === "manage_project_documents" || tool === "apply_manuscript_edits") {
    if (!manageProjectDocumentsSummaryIndicatesManuscriptApplied(outcome ?? "")) return;
  } else {
    return;
  }
  window.dispatchEvent(new CustomEvent("sudo-agent-edit-document-record-undo", { bubbles: false }));
}

type ProseMirrorDocJson = {
  type?: string;
  content?: unknown[];
};

/** Apply a captured TipTap JSON snapshot through the live collaboration Y.Doc. */
export function applyAgentManuscriptUndoSnapshotToCollaborationYdoc(
  ydoc: Y.Doc,
  snapshot: unknown,
): boolean {
  if (!ydoc || !snapshot || typeof snapshot !== "object") return false;
  const docNode = snapshot as ProseMirrorDocJson;
  if (docNode.type !== "doc" || !Array.isArray(docNode.content) || docNode.content.length === 0) {
    return false;
  }

  const schema = getProseMirrorSchema();
  const sanitizedDoc = stripUnknownMarks(docNode, schema);
  const tempYdoc = TiptapTransformer.toYdoc(sanitizedDoc, "default", extensions);
  try {
    const sourceFragment = tempYdoc.getXmlFragment("default");
    const children = sourceFragment.slice(0, sourceFragment.length).map((node) => node.clone());
    if (children.length === 0) return false;

    const targetFragment = ydoc.getXmlFragment("default");
    ydoc.transact(() => {
      targetFragment.delete(0, targetFragment.length);
      targetFragment.insert(0, children);
    });
    return true;
  } finally {
    tempYdoc.destroy();
  }
}

export function resolveCollaborationYdoc(
  editor: { storage?: { collaboration?: { document?: Y.Doc } }; extensionStorage?: { collaboration?: { document?: Y.Doc } } } | null | undefined,
  editorModel?: { ydoc?: Y.Doc } | null,
): Y.Doc | null {
  return (
    editorModel?.ydoc
    ?? editor?.storage?.collaboration?.document
    ?? editor?.extensionStorage?.collaboration?.document
    ?? null
  );
}

export function serializeEditorDocJson(editor: { getJSON(): unknown } | null | undefined): string {
  if (!editor) return "";
  return JSON.stringify(editor.getJSON());
}

/** True when the live editor still differs from the captured pre-edit snapshot. */
export function editorDocDiffersFromUndoSnapshot(
  editor: { getJSON(): unknown } | null | undefined,
  snapshot: unknown,
): boolean {
  if (!editor || !snapshot) return false;
  return serializeEditorDocJson(editor) !== JSON.stringify(snapshot);
}

export function editorDocMatchesUndoSnapshot(
  editor: { getJSON(): unknown } | null | undefined,
  snapshot: unknown,
): boolean {
  if (!editor || !snapshot) return false;
  return serializeEditorDocJson(editor) === JSON.stringify(snapshot);
}

/** Pending snapshot captured after the edit landed — useless for undo and must not be committed. */
export function undoSnapshotMatchesCurrentEditorDoc(
  editor: { getJSON(): unknown } | null | undefined,
  snapshot: unknown,
): boolean {
  return editorDocMatchesUndoSnapshot(editor, snapshot);
}

export async function waitForEditorDocToMatchUndoSnapshot(
  editor: { getJSON(): unknown },
  snapshot: unknown,
  options?: { maxAttempts?: number; intervalMs?: number },
): Promise<boolean> {
  const maxAttempts = options?.maxAttempts ?? 24;
  const intervalMs = options?.intervalMs ?? 50;
  const targetJson = JSON.stringify(snapshot);
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (serializeEditorDocJson(editor) === targetJson) return true;
    await new Promise((resolve) => {
      window.setTimeout(resolve, intervalMs);
    });
  }
  return false;
}

export const MANUSCRIPT_UNDO_CAPTURE_OPERATIONS = new Set([
  "edit_document",
  "review_edit_document",
  "edit_document_batch",
  "find_replace_document",
  "create_document",
]);

export function manuscriptUndoCaptureOperation(operation: string | undefined): boolean {
  return MANUSCRIPT_UNDO_CAPTURE_OPERATIONS.has(String(operation ?? "").trim());
}
